import moment from 'moment'

const programmingOpions = [{ label: 'Meta de cumplimiento', value: 'goals' }, { label: 'Días y horas', value: 'daysHours' }]
const daysOptions = [
  { label: 'Do', complete: 'domingo', value: 'sunday' },
  { label: 'Lu', complete: 'lunes', value: 'monday' },
  { label: 'Ma', complete: 'martes', value: 'tuesday' },
  { label: 'Mi', complete: 'miércoles', value: 'wednesday' },
  { label: 'Ju', complete: 'jueves', value: 'thursday' },
  { label: 'Vi', complete: 'viernes', value: 'friday' },
  { label: 'Sá', complete: 'sábado', value: 'saturday' }
]
const ordinalOptions = [
  { label: 'primer', value: 'first', num: 0 },
  { label: 'segundo', value: 'second', num: 1 },
  { label: 'tercer', value: 'third', num: 2 },
  { label: 'cuarto', value: 'fourth', num: 3 },
  { label: 'ultimo', value: 'last', num: 4 }
]
const frequencyOptions = [
  { label: 'diariamente', value: 'daily', single: 'día', multiple: 'días' },
  { label: 'semanalmente', value: 'weekly', single: 'semana', multiple: 'semanas' },
  { label: 'mensualmente', value: 'monthly', single: 'mes', multiple: 'meses' }
]
const priorityOptions = [
  { label: 'Baja', value: 3, color: 'green' },
  { label: 'Media', value: 2, color: 'orange' },
  { label: 'Alta', value: 1, color: 'red' }
]

const FROZEN_EDIT_MESSAGE = 'La edición de esta actividad sigue deshabilitada mientras se libera la nueva ' +
  'versión del módulo de Rutinas. Puedes crear, programar y finalizar rutinas con normalidad.'

// TKT-2245 — capacidad que descongela la edición individual de cada tipo de
// programación. Espeja App\Services\Routines\RoutineCapabilityFlag del backend: la
// decisión es suya, aquí sólo se lee cuál de las dos llaves mirar.
const EDIT_CAPABILITY_BY_PROGRAMMING_TYPE = {
  goals: 'edit_activity_goals',
  daysHours: 'edit_activity_days_hours'
}

// Misma partición que usa la generación en el backend: ramifica con `=== 'goals'` y todo
// lo demás es el plano por días y horas. Hay actividades con valores heredados que la
// generación trata como días y horas; exigir literalmente 'daysHours' las dejaría fuera.
const isDaysHoursPlane = (programmingType) => Boolean(programmingType) && programmingType !== 'goals'

export default {
  daysOptions: () => daysOptions,
  /**
   * TKT-2245 F4 — ¿se puede editar esta actividad?
   *
   * Una actividad que nunca generó (`last_generated` nulo) es parte del flujo de
   * creación y nunca estuvo congelada. Una ya generada sólo se edita si el backend
   * autorizó la capacidad de SU tipo de programación: la Fase 4 abre `goals` y la
   * Fase 5 abrirá `daysHours`, por separado.
   */
  canEditActivity: (state) => (event) => {
    if (!event) return false
    if (!event.last_generated) return true

    const capability = EDIT_CAPABILITY_BY_PROGRAMMING_TYPE[event.programming_type] ||
      (isDaysHoursPlane(event.programming_type) ? EDIT_CAPABILITY_BY_PROGRAMMING_TYPE.daysHours : null)

    return Boolean(capability && state.routineCapabilities[capability])
  },
  frozenEditMessage: () => FROZEN_EDIT_MESSAGE,
  /**
   * TKT-2245 F2/F3 — plano global de una rutina activa: usuarios y vigencia se reactivan
   * cada uno con su capacidad (`edit_routine_users` / `edit_routine_dates`). Espejo de lo
   * que el backend publica; quien decide es `RoutinesController@updateRoutine`.
   */
  canEditRoutineUsers: (state) => Boolean(state.routineCapabilities.edit_routine_users),
  canEditRoutineDates: (state) => Boolean(state.routineCapabilities.edit_routine_dates),
  /**
   * TKT-2245 F6 — ¿se puede tocar "Configuración específica por día"?
   *
   * Sólo existe en días y horas. Al CREAR nunca estuvo congelada, así que la capacidad
   * sólo manda al editar una actividad ya activa; `isActive` es quien distingue los dos
   * momentos y lo aporta el diálogo, no el store.
   */
  canEditDayConfig: (state) => (programmingType) => {
    return isDaysHoursPlane(programmingType) && Boolean(state.routineCapabilities.routine_day_config)
  },
  /**
   * TKT-2245 F7 — ¿y "Múltiples visitas por día"?
   *
   * Depende de la Fase 6: el toggle vive anidado dentro del otro y no existe sin él.
   * Espeja `RoutineCapabilityFlag::canEditMultipleVisits()` del backend, que es quien
   * decide de verdad; aquí sólo se dibuja.
   */
  canEditMultipleVisits: (state) => (programmingType) => {
    return isDaysHoursPlane(programmingType) &&
      Boolean(state.routineCapabilities.routine_day_config) &&
      Boolean(state.routineCapabilities.routine_multiple_visits)
  },
  ordinalOptions: () => ordinalOptions,
  frequencyOptions: () => frequencyOptions,
  priorityOptions: () => priorityOptions,
  locationsLength: () => (locations) => {
    const idsSet = new Set()

    locations.forEach(str => {
      const ids = str.split('-')
      const lastId = ids[ids.length - 1]
      idsSet.add(lastId)
    })

    return Array.from(idsSet).length
  },
  programmingType (state) {
    const programmingType = state.newEvent.programmingType
    return programmingType ? programmingOpions.find(prog => prog.value === programmingType).label : ''
  },
  goalLabel (state) {
    const event = state.newEvent
    const times = event.goal === '1' ? 'vez' : 'veces'
    const frequency = event.frequency === 'weekly' ? 'semana' : 'mes'
    const forType = event.frequency === 'daily' ? 'día' : frequency
    return `El formulario se realizará ${event.goal} ${times} por ${forType} en cada una de las ubicaciones seleccionadas.`
  },
  labelGoal (state) {
    const event = state.newEvent
    if (event.programmingType !== 'goals') return ''

    const times = event.goal === '1' ? 'vez' : 'veces'
    return `${event.goal} ${times} ${frequencyOptions.find(op => op.value === event.frequency).label}`
  },
  labelByRange (state) {
    const event = state.newEvent
    const days = event.days
    if (event.programmingType === 'goals') return ''
    if (event.frequency === 'daily') return 'todos los días'
    if (event.frequency === 'monthly') {
      if (event.monthlyType === 'the') {
        const ordinal = ordinalOptions.find(op => op.value === event.ordinal).label
        const day = event.day.complete
        return `todos los meses el ${ordinal} ${day}`
      } else {
        // Sin dias elegidos la frase quedaba coja ("los días  mensualmente"): se dice lo
        // unico que se sabe. Y "díás" era una errata visible en pantalla.
        if (!days || days.length === 0) return 'mensualmente'

        return `los días ${state.getDaysLabel(days, true)} mensualmente`
      }
    }

    if (days.length === 0) return 'todas las semanas'
    if (days.length === 1) return `todas las semanas los días ${daysOptions.find(day => day.value === days[0]).complete}`

    return `todas las semanas los días ${state.getDaysLabel(days)}`
  },
  getDaysLabel: () => (days, isNumbers = false) => {
    if (days.length === 0) return ''

    let label = ''
    if (isNumbers) label = `${days[0]}`
    else label = `${daysOptions.find(day => day.value === days[0]).complete}`

    days.forEach((day, i) => {
      if (i > 0) {
        if (i === days.length - 1) label = `${label} y `
        else label = `${label}, `

        if (isNumbers) label = `${label} ${day}`
        else label = `${label} ${daysOptions.find(d => d.value === day).complete}`
      }
    })

    return label
  },
  getStartDate: () => (now, startRoutine, frequency) => {
    const momentNow = moment(now)
    const momentStartRoutine = moment(startRoutine)

    if (momentStartRoutine.isSame(momentNow, 'day')) return momentNow

    let targetDate

    switch (frequency) {
      case 'daily': {
        return momentNow
      }
      case 'weekly': {
        const dayOfWeekStart = momentStartRoutine.day()
        const dayOfWeekNow = momentNow.day()

        if (dayOfWeekNow === dayOfWeekStart) targetDate = momentNow.clone()
        else {
          const dayName = momentStartRoutine.format('dddd')
          targetDate = momentNow.clone().day(dayName)
        }
        return targetDate
      }
      case 'monthly': {
        const dayNumber = momentStartRoutine.date()
        if (momentNow.date() < dayNumber) targetDate = momentNow.clone().subtract(1, 'month').startOf('month').add(dayNumber - 1, 'days')
        else targetDate = momentNow.clone().startOf('month').add(dayNumber - 1, 'days')

        return targetDate
      }
      default: {
        return momentNow
      }
    }
  },
  getScoreColor: () => (score) => {
    if (score >= 90) return '#46AF51'
    else if (score >= 70) return '#F2B94B'
    return '#DD3A23'
  }
}
